import React, { useContext, FormEventHandler, useState, useRef } from 'react'
import {
  Container,
  Row,
  Col,
  CaptionText,
  Link,
  IconButton,
  EditIcon,
} from '@paypalcorp/pp-react'
import {
  Context as WorldReadyContext,
  Message,
} from '@paypalcorp/worldready-react'
import GroupTitle from './GroupTitle'
import GroupDescription from './GroupDescription'
import GroupCloseDate from './GroupCloseDate'
import GroupGoalAmount from './GroupGoalAmount'
import {
  GetContentFunction,
  useLocalizedContent,
} from '../../../hooks/useLocalizedContent'
import { useGetEligibilityQuery } from '../../../services/checkEligibility'
import { formatCurrency } from '../../../utils/localization/format_worldready'
import { REGEX } from '../../../constants'
import { getTermsAndConditionsLink } from '../../../utils/links/termsLink'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  selectBackgroundImageUrl,
  selectGroupForm,
  setCloseDate,
  setDescription,
  setGoalAmount,
  setTitle,
} from '../../../store/slices/groupFormSlice'
import { clearHandledError } from '../../../store/slices/handledErrorSlice'
import { parseDateString } from '../../../utils/localization/parseDateString'
import * as styles from './groupFormFields.css'
import { toggleUpdateCoverImage } from '../../../store/slices/sheetsSlice'
import { isCoverOnCreateFlowEnabled } from '../../../utils/features'
import { tracking } from '../../../lib/analytics/fpti'
import Events from '../../../lib/analytics/eventTags.js'
import serverData from '../../../utils/server-data'

interface Props {
  displayTerms?: boolean
  handleSubmit: Function
  onClickEditImage: () => void
}

const getErrorsInitialState = () => ({
  title: '',
  description: '',
  closeDate: '',
  goalAmount: '',
})

const getMonthsDifferenceFromToday = (dateTo: Date): number => {
  const currentDate = new Date()

  return (
    dateTo.getMonth() -
    currentDate.getMonth() +
    12 * (dateTo.getFullYear() - currentDate.getFullYear())
  )
}

const getInvalidFutureCloseDateErrorMessage = (
  maxDate: Date,
  getContent: GetContentFunction,
): string => {
  const monthsDiff = getMonthsDifferenceFromToday(maxDate)

  // Show difference in months
  if (monthsDiff < 12) {
    return getContent('error.invalidCloseDateMonths', {
      months: monthsDiff,
    })
  }

  // Show difference in years
  return getContent('error.invalidCloseDate')
}

export const GroupFormFields = ({
  displayTerms,
  handleSubmit,
  onClickEditImage,
}: Props) => {
  const titleRef = useRef<HTMLTextAreaElement | null>()
  const descriptionRef = useRef<HTMLTextAreaElement | null>()
  const closeDateRef = useRef<HTMLInputElement | null>()
  const goalAmountRef = useRef<HTMLInputElement | null>()
  const backgroundImageUrl = useAppSelector(selectBackgroundImageUrl)
  const worldReadyC = useContext(WorldReadyContext)
  const dispatch = useAppDispatch()
  const { getContent } = useLocalizedContent()
  const [errors, setErrors] = useState(getErrorsInitialState())
  const { data } = useGetEligibilityQuery(null)
  const groupForm = useAppSelector(selectGroupForm)
  const configuration =
    data?.customer?.moneyPools?.moneyPoolEligibility?.configuration
  const maxDateString = configuration?.maximumTargetDate
  const maxDateStringFormat = configuration?.supportedTargetDateFormat
  const maxDate =
    maxDateStringFormat === 'DD/MM/YYYY'
      ? parseDateString(maxDateString)
      : new Date(maxDateString)

  const maximumGoalAmount = Number(
    configuration?.maximumTargetAmount?.value || 0,
  )
  const maximumGoalCurrencyCode =
    configuration?.maximumTargetAmount?.currencyCode
  const formattedMaximumGoalAmount = formatCurrency({
    worldReady: worldReadyC,
    value: Number(maximumGoalAmount),
    currencyCode: maximumGoalCurrencyCode,
    additionalConfig: {
      maximumFractionDigits: 0,
    },
  })

  const getTitleCharsErrorMessage = () => {
    return isCoverOnCreateFlowEnabled()
      ? getContent('error.titleMaxCharacters')
      : getContent('error.titleExceededCharacters')
  }

  const getDescriptionCharsErrorMessage = () => {
    return isCoverOnCreateFlowEnabled()
      ? getContent('error.descriptionMaxCharacters')
      : getContent('error.descriptionExceededCharacters')
  }

  const validate = () => {
    const newErrors = getErrorsInitialState()
    const titleRegex = new RegExp(REGEX.TITLE)
    const descriptionRegex = new RegExp(REGEX.DESCRIPTION)
    const selectedCloseDateTime =
      groupForm.closeDate && new Date(groupForm.closeDate).getTime()
    const maxDateTime = maxDate && maxDate.getTime()

    if (!groupForm.title?.trim()) {
      newErrors.title = getContent('error.emptyTitle')
    } else if (groupForm.title.length > 70) {
      newErrors.title = getTitleCharsErrorMessage()
    } else if (!titleRegex.test(groupForm.title)) {
      newErrors.title = getContent('error.invalidTitle')
    }

    if (!groupForm.description?.trim()) {
      newErrors.description = getContent('error.emptyDescription')
    } else if (groupForm.description.length > 250) {
      newErrors.description = getDescriptionCharsErrorMessage()
    } else if (!descriptionRegex.test(groupForm.description)) {
      newErrors.description = getContent('error.invalidDescription')
    }

    if (!groupForm.closeDate) {
      newErrors.closeDate = getContent('error.emptyCloseDate')
    } else if (maxDateTime && selectedCloseDateTime > maxDateTime) {
      newErrors.closeDate = getInvalidFutureCloseDateErrorMessage(
        maxDate,
        getContent,
      )
    } else if (selectedCloseDateTime < new Date().getTime()) {
      newErrors.closeDate = getContent('error.closeDateInPast')
    }

    if (
      groupForm.goalAmount &&
      Number(groupForm.goalAmount) > maximumGoalAmount
    ) {
      newErrors.goalAmount = getContent('error.goalAmountExceeded', {
        formattedMaximumGoalAmount,
      })
    }

    return newErrors
  }

  const handleFormSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    dispatch(clearHandledError())
    event.preventDefault()
    const validationErrors = validate()
    const hasErrors = Object.values(validationErrors).some(
      (error) => error !== '',
    )

    // Show the errors or hide them before submitting to keep the UI clean
    setErrors(validationErrors)
    if (hasErrors) {
      if (validationErrors.title && titleRef.current) {
        titleRef.current.focus()
      } else if (validationErrors.description && descriptionRef.current) {
        descriptionRef.current.focus()
      } else if (validationErrors.closeDate && closeDateRef.current) {
        closeDateRef.current.focus()
      } else if (validationErrors.goalAmount && goalAmountRef.current) {
        goalAmountRef.current.focus()
      }
      return
    }

    handleSubmit(event)
  }

  const handleTitleChange = (value: string) => {
    dispatch(setTitle(value))
  }

  const handleDescriptionChange = (value: string) => {
    dispatch(setDescription(value))
  }

  const handleCloseDateChange = (value: string) => {
    dispatch(setCloseDate(value))
  }

  const handleGoalAmountChange = (value: string) => {
    dispatch(setGoalAmount(value))
  }

  const handleClickOnEditImage = () => {
    tracking(
      Events.p2p_pools_edit_photo_pressed({
        source: serverData.trackingParameters.externalSource,
        page_variant: 'creator',
        pool_id: groupForm.id,
      }),
    )
    dispatch(toggleUpdateCoverImage())
    onClickEditImage()
  }

  return (
    <form id="groupForm" data-testid="group-form" onSubmit={handleFormSubmit}>
      <Container form>
        {isCoverOnCreateFlowEnabled() && (
          <Row>
            <Col form="full">
              <img
                alt=""
                src={backgroundImageUrl}
                className={styles.coverPhoto}
                aria-hidden="true"
              />
              <IconButton
                aria-label={getContent('create.cover.image.edit')}
                buttonType="secondary"
                size="sm"
                svgIconComponent={EditIcon}
                className={styles.editButton}
                type="button"
                onClick={handleClickOnEditImage}
                data-testid="editImageButton"
              />
            </Col>
          </Row>
        )}
        <Row>
          <Col form="full">
            <GroupTitle
              data-testid="group-title-input"
              error={errors.title}
              handleOnChange={handleTitleChange}
              titleRef={titleRef}
            />
          </Col>
        </Row>
        <Row>
          <Col form="full">
            <GroupDescription
              error={errors.description}
              handleOnChange={handleDescriptionChange}
              descriptionRef={descriptionRef}
            />
          </Col>
        </Row>
        <Row>
          <Col form="full">
            <GroupCloseDate
              error={errors.closeDate}
              maxDate={maxDate}
              handleOnChange={handleCloseDateChange}
              closeDateRef={closeDateRef}
            />
          </Col>
        </Row>
        <Row>
          <Col form="full">
            <GroupGoalAmount
              error={errors.goalAmount}
              currencyCode={maximumGoalCurrencyCode}
              handleOnChange={handleGoalAmountChange}
              goalAmountRef={goalAmountRef}
            />
          </Col>
        </Row>

        {displayTerms && (
          <Row>
            <Col form="full">
              <CaptionText>
                <Message
                  id="create.view.termsNConditons"
                  poolTerms={(innerMessage: string) => (
                    <Link
                      target="_blank"
                      href={getTermsAndConditionsLink()}
                      size="sm"
                      data-testid="poolTerms"
                    >
                      {innerMessage}
                    </Link>
                  )}
                />
              </CaptionText>
            </Col>
          </Row>
        )}
      </Container>
    </form>
  )
}
